import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import AppHeader from "../components/AppHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import CategoryEditor from "../components/CategoryEditor";
import { Volleyball, ArrowLeft, Sparkles, Plus, Trash2 } from "lucide-react";

export default function CreateQuick() {
    const nav = useNavigate();
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        name: "",
        tipo_voley: "Sala",
        canchas: 2,
        hora_inicio: "09:00",
        hora_fin: "21:00",
        duracion_bloque: 45,
        dias_cfg: [],
        categorias: [],
    });

    const update = (k, v) => setForm({ ...form, [k]: v });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error("Ponle un nombre al torneo");
        if (form.categorias.length === 0)
            return toast.error("Agrega al menos una categoría");
        const invalid = form.categorias.find(
            (c) => !c.nombre.trim() || c.teams.length < 2,
        );
        if (invalid)
            return toast.error(
                "Cada categoría necesita un nombre y al menos 2 equipos",
            );

        setLoading(true);
        try {
            const payload = {
                ...form,
                canchas: parseInt(form.canchas) || 1,
                duracion_bloque: parseInt(form.duracion_bloque) || 45,
                categorias: form.categorias.map((c) => ({
                    nombre: c.nombre,
                    rama: c.rama,
                    formato: c.formato,
                    num_grupos: c.num_grupos,
                    teams: c.teams,
                })),
            };
            const { data } = await api.post("/tournaments/quick", payload);
            toast.success("Torneo creado");
            nav(`/t/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <AppHeader />
            <main className="max-w-4xl mx-auto px-6 py-10">
                <button
                    onClick={() => nav("/dashboard")}
                    className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm mb-6"
                    data-testid="back-dashboard"
                >
                    <ArrowLeft className="w-4 h-4" /> Volver al panel
                </button>

                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-yellow-300 grid place-items-center">
                        <Volleyball className="w-6 h-6 text-rose-500" />
                    </div>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-700">
                            Modo relámpago
                        </p>
                        <h1 className="font-display text-3xl font-black">
                            Crear Torneo Rápido
                        </h1>
                    </div>
                </div>

                <form onSubmit={submit} className="mt-8 space-y-6">
                    <div className="vp-card p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="md:col-span-2 space-y-2">
                            <Label>Nombre del torneo</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => update("name", e.target.value)}
                                placeholder="Copa Relámpago Sub-20"
                                className="h-12 rounded-xl"
                                data-testid="quick-name"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>N° de canchas</Label>
                            <Input
                                type="number"
                                min={1}
                                value={form.canchas}
                                onChange={(e) =>
                                    update("canchas", e.target.value)
                                }
                                className="h-12 rounded-xl"
                                data-testid="quick-courts"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Hora de inicio</Label>
                            <Input
                                type="time"
                                value={form.hora_inicio}
                                onChange={(e) =>
                                    update("hora_inicio", e.target.value)
                                }
                                className="h-12 rounded-xl"
                                data-testid="quick-start-time"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Hora de cierre</Label>
                            <Input
                                type="time"
                                value={form.hora_fin}
                                onChange={(e) =>
                                    update("hora_fin", e.target.value)
                                }
                                className="h-12 rounded-xl"
                                data-testid="quick-end-time"
                            />
                            <p className="text-[10px] text-slate-500">
                                Si los partidos no caben en un día, se reparten
                                en Día 2, Día 3, etc.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label>Duración por partido (min)</Label>
                            <Input
                                type="number"
                                min={15}
                                step={5}
                                value={form.duracion_bloque}
                                onChange={(e) =>
                                    update("duracion_bloque", e.target.value)
                                }
                                className="h-12 rounded-xl"
                                data-testid="quick-duration"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Tipo</Label>
                            <Input
                                value={form.tipo_voley}
                                onChange={(e) =>
                                    update("tipo_voley", e.target.value)
                                }
                                placeholder="Sala / Playa"
                                className="h-12 rounded-xl"
                                data-testid="quick-type"
                            />
                        </div>
                    </div>

                    <div className="vp-card p-6">
                        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                            <div>
                                <h2 className="font-display text-xl font-bold">
                                    Horario por día (opcional)
                                </h2>
                                <p className="text-sm text-slate-500">
                                    Si tu torneo abarca varios días con
                                    horarios distintos (sábado 12:00, domingo
                                    08:00), configúralo aquí. Deja vacío para
                                    usar el mismo rango horario todos los
                                    días.
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    update("dias_cfg", [
                                        ...(form.dias_cfg || []),
                                        {
                                            hora_inicio: form.hora_inicio,
                                            hora_fin: form.hora_fin,
                                        },
                                    ])
                                }
                                className="rounded-xl"
                                data-testid="quick-add-day"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Agregar día
                            </Button>
                        </div>
                        {(form.dias_cfg || []).length === 0 ? (
                            <div className="text-sm text-slate-400 italic py-3">
                                Sin configuración específica. Se usará{" "}
                                {form.hora_inicio}–{form.hora_fin} en todos
                                los días.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {(form.dias_cfg || []).map((d, i) => (
                                    <div
                                        key={i}
                                        className="p-3 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-3 flex-wrap"
                                        data-testid={`quick-day-${i}`}
                                    >
                                        <span className="text-xs uppercase font-bold tracking-widest text-slate-500 min-w-[64px]">
                                            Día {i + 1}
                                        </span>
                                        <Input
                                            type="time"
                                            value={d.hora_inicio}
                                            onChange={(e) => {
                                                const arr = [
                                                    ...(form.dias_cfg || []),
                                                ];
                                                arr[i] = {
                                                    ...arr[i],
                                                    hora_inicio: e.target.value,
                                                };
                                                update("dias_cfg", arr);
                                            }}
                                            className="h-10 rounded-xl w-32"
                                            data-testid={`quick-day-${i}-ini`}
                                        />
                                        <span className="text-xs text-slate-400">
                                            a
                                        </span>
                                        <Input
                                            type="time"
                                            value={d.hora_fin}
                                            onChange={(e) => {
                                                const arr = [
                                                    ...(form.dias_cfg || []),
                                                ];
                                                arr[i] = {
                                                    ...arr[i],
                                                    hora_fin: e.target.value,
                                                };
                                                update("dias_cfg", arr);
                                            }}
                                            className="h-10 rounded-xl w-32"
                                            data-testid={`quick-day-${i}-fin`}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                const arr = [
                                                    ...(form.dias_cfg || []),
                                                ];
                                                arr.splice(i, 1);
                                                update("dias_cfg", arr);
                                            }}
                                            className="ml-auto text-rose-600 hover:bg-rose-50 rounded-lg"
                                            data-testid={`quick-day-${i}-remove`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="vp-card p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Sparkles className="w-5 h-5 text-ipn-800" />
                            <h2 className="font-display text-xl font-bold">
                                Categorías
                            </h2>
                        </div>
                        <CategoryEditor
                            value={form.categorias}
                            onChange={(v) => update("categorias", v)}
                        />
                    </div>

                    <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => nav("/dashboard")}
                            className="h-12 rounded-xl"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="h-12 rounded-xl bg-ipn-800 hover:bg-ipn-900 px-6 font-bold"
                            data-testid="quick-submit"
                        >
                            {loading ? "Creando…" : "Generar partidos"}
                        </Button>
                    </div>
                </form>
            </main>
        </div>
    );
}
